import React, { useState, useRef, KeyboardEvent } from 'react'
import { motion } from 'framer-motion'

interface ChatInputProps {
  onSendMessage: (message: string) => void
  disabled?: boolean
  placeholder?: string
  maxLength?: number
  className?: string
}

export const ChatInput: React.FC<ChatInputProps> = ({
  onSendMessage,
  disabled = false,
  placeholder = 'Type your message...',
  maxLength = 1000,
  className = ''
}) => {
  const [message, setMessage] = useState('')
  const [isSending, setIsSending] = useState(false)
  const inputRef = useRef<HTMLTextAreaElement>(null)

  const handleSend = async () => {
    const trimmedMessage = message.trim()
    if (!trimmedMessage || disabled || isSending) return

    setIsSending(true)
    try {
      await onSendMessage(trimmedMessage)
      setMessage('')
      // Reset textarea height
      if (inputRef.current) {
        inputRef.current.style.height = 'auto'
      }
    } catch (error) {
      console.error('Failed to send message:', error)
    } finally {
      setIsSending(false)
    }
  }

  const handleKeyPress = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSend()
    }
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value
    if (value.length <= maxLength) {
      setMessage(value)
    }

    // Auto-resize textarea
    const textarea = e.target
    textarea.style.height = 'auto'
    textarea.style.height = `${Math.min(textarea.scrollHeight, 120)}px`
  }

  const sendButtonVariants = {
    idle: { scale: 1, rotate: 0 },
    hover: { scale: 1.05 },
    tap: { scale: 0.95 },
    sending: { rotate: 180, scale: 0.9 }
  }

  return (
    <div className={`bg-white border-t border-gray-200 p-4 ${className}`}>
      <div className="flex items-end space-x-3">
        {/* Message input */}
        <div className="flex-1 relative">
          <textarea
            ref={inputRef}
            value={message}
            onChange={handleInputChange}
            onKeyPress={handleKeyPress}
            placeholder={placeholder}
            disabled={disabled || isSending}
            rows={1}
            className="
              w-full px-4 py-3 pr-12
              border border-gray-300 rounded-2xl
              resize-none overflow-hidden
              focus:ring-2 focus:ring-blue-500 focus:border-transparent
              disabled:bg-gray-50 disabled:text-gray-500
              text-sm leading-relaxed
              transition-all duration-200
            "
            style={{ minHeight: '44px', maxHeight: '120px' }}
          />
          
          {/* Character counter */}
          {message.length > maxLength * 0.8 && (
            <div className="absolute bottom-1 right-12 text-xs text-gray-500">
              {message.length}/{maxLength}
            </div>
          )}
        </div>

        {/* Send button */}
        <motion.button
          onClick={handleSend}
          disabled={!message.trim() || disabled || isSending}
          variants={sendButtonVariants}
          initial="idle"
          animate={isSending ? "sending" : "idle"}
          whileHover={!disabled && !isSending ? "hover" : "idle"}
          whileTap={!disabled && !isSending ? "tap" : "idle"}
          className={`
            flex items-center justify-center
            w-11 h-11 rounded-full
            transition-all duration-200
            ${message.trim() && !disabled && !isSending
              ? 'bg-blue-500 hover:bg-blue-600 text-white shadow-md hover:shadow-lg'
              : 'bg-gray-200 text-gray-400 cursor-not-allowed'
            }
          `}
          aria-label="Send message"
        >
          {isSending ? (
            <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
              <circle 
                className="opacity-25" 
                cx="12" 
                cy="12" 
                r="10" 
                stroke="currentColor" 
                strokeWidth="4"
              />
              <path 
                className="opacity-75" 
                fill="currentColor" 
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
          ) : (
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
            </svg>
          )}
        </motion.button>
      </div>

      {/* Helper text */}
      <div className="mt-2 text-xs text-gray-500 px-1">
        Press Enter to send • Shift+Enter for new line
      </div>
    </div>
  )
}

export default ChatInput